import React, { useState } from 'react';

import { Image } from '@codes/ui-kit/components/ui/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from '@codes/ui-kit';

interface CatalogImageSliderProps {
    images: string[];
    name: string;
    stereo: string;
    iconImage: string;
}

export const CatalogImageSlider: React.FC<CatalogImageSliderProps> = ({ images, name, stereo, iconImage }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isZoomed, setIsZoomed] = useState(false);

    const hasImages = images && images.length > 0;
    const hasMultiple = images && images.length > 1;

    const goTo = (index: number) => {
        const len = hasImages ? images.length : 1;
        setCurrentIndex(((index % len) + len) % len);
    };

    const openModal = () => {
        setIsModalOpen(true);
        setIsZoomed(false);
    };

    return (
        <>
            <div className="relative bg-box dark:bg-card w-full aspect-[452/304] border border-border dark:border-border rounded-lg overflow-hidden">
                {hasImages ? (
                    <Image
                        src={images[currentIndex]}
                        alt={`${name} - Image ${currentIndex + 1}`}
                        className="w-full h-full object-cover cursor-zoom-in"
                        onClick={openModal}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Image src={iconImage} alt={stereo} />
                    </div>
                )}

                {hasMultiple && (
                    <>
                        <button
                            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors"
                            onClick={() => goTo(currentIndex - 1)}
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors"
                            onClick={() => goTo(currentIndex + 1)}
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                            {images.map((_, i) => (
                                <button
                                    key={i}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                        i === currentIndex ? 'bg-card' : 'bg-card/40'
                                    }`}
                                    onClick={() => goTo(i)}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogTitle />
                <DialogContent className="max-w-[90vw] max-h-[90vh] p-0" aria-describedby={undefined}>
                    <div className="relative bg-box w-full aspect-[1] overflow-hidden">
                        {hasImages && (
                            <Image
                                src={images[currentIndex]}
                                alt={`${name} - Image ${currentIndex + 1}`}
                                className={`w-full h-full object-contain transition-transform duration-300 ${
                                    isZoomed ? 'scale-150 cursor-zoom-out' : 'cursor-zoom-in'
                                }`}
                                onDoubleClick={() => setIsZoomed(!isZoomed)}
                            />
                        )}
                        {hasMultiple && (
                            <>
                                <button
                                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors"
                                    onClick={() => goTo(currentIndex - 1)}
                                >
                                    <ChevronLeft className="w-6 h-6" />
                                </button>
                                <button
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors"
                                    onClick={() => goTo(currentIndex + 1)}
                                >
                                    <ChevronRight className="w-6 h-6" />
                                </button>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
